// ============================================
// REDUCER - BOOK VECTORIZATION
// ============================================
import { bookVectorizationActions } from '../actions'
import ValidateFormObject from '../utils/ValidateFormObject'

// ============================================
// 📥 IMPORTA CONSTANTES DAS ACTIONS
// ============================================
const {
  FETCH_BOOK_VECTORIZATION,
  FETCHING_BOOK_VECTORIZATION,
  FETCH_BOOK_VECTORIZATION_ERROR,
  FETCH_BOOK_VECTORIZATIONS,
  FETCHING_BOOK_VECTORIZATIONS,
  FETCH_BOOK_VECTORIZATIONS_ERROR,
  OPEN_MODAL,
  CLOSE_MODAL,
  CHANGE_BOOK_VECTORIZATION,
  DELETE_BOOK_VECTORIZATION,
  ADD_BOOK_VECTORIZATION,
  EDIT_BOOK_VECTORIZATION,
  FORM_TOUCHED,
  NEW_FORM_BOOK_VECTORIZATION,
  BOOK_VECTORIZATION_FORM_ERROR,
  CHANGE_PAGE,
  CHANGE_PER_PAGE,
  CHANGE_ORDER_COLUMN,
  CHANGE_ORDER_DIRECTION,
  RESET_STATE,
} = bookVectorizationActions

// ============================================
// 🏗️ ESTADO INICIAL
// ============================================
const initialState = {
  // Loading states
  loadingBookVectorization: false,
  loadingBookVectorizations: false,
  fetchBookVectorizationError: false,
  fetchBookVectorizationsError: false,

  // Lista de livros
  bookVectorizations: [],

  // Ordenação
  order: {
    column: 'book_name',
    direction: 1, // 1 = asc, -1 = desc
  },

  // Paginação
  bookVectorizationsPagination: {
    page: 0, // 0-indexed no frontend
    rowsPerPage: 50,
    length: 0,
  },

  // Livro individual (usado no modal)
  bookVectorization: {
    // Campos principais OBRIGATÓRIOS
    id: null,
    book_name: '',
    isbn: '',
    created_at: null,
    vectorized_at: null,
    arquivo: null,

    // Campos OPCIONAIS (novos)
    autores: [], // Array de strings
    ano: null, // Number
    especialidade: '', // String

    // Estrutura de processamento (compatibilidade visual)
    processamento: {
      id: null,
      arquivo_nome: '',
      status_atual: 'pendente',
      ciclo_uuid: null,
      criado_em: null,
      atualizado_em: null,
    },

    // Estrutura de arquivo
    arquivo_info: {
      tamanho_bytes: 0,
      criado_em: null,
    },

    // Estrutura de status
    status: {
      id: null,
      status: 'pendente',
      ativo: true,
      iniciado_em: null,
      finalizado_em: null,
    },
  },

  // Validação do formulário
  bookVectorizationFormErrors: {},
  bookVectorizationFormTouched: false,

  // Modal
  modalOpen: false,
}

// ============================================
// 📋 SCHEMA DE VALIDAÇÃO
// ============================================
const bookVectorizationSchema = {
  // Schema para ADICIONAR novo livro
  add: {
    book_name: {
      required: true,
      minValue: 3,
      maxValue: 200,
      errorMessage: 'Nome do livro deve ter entre 3 e 200 caracteres.',
    },
    isbn: {
      required: true,
      minValue: 10,
      maxValue: 17,
      errorMessage: 'ISBN deve ter entre 10 e 17 caracteres.',
    },
    arquivo: {
      required: true,
      errorMessage: 'Arquivo EPUB ou PDF é obrigatório.',
    },
    // Autores, Ano e Especialidade são opcionais, então não precisam estar no schema
    // a menos que queira validar tipo/tamanho específico.
  },

  // Schema para EDITAR (mantido para compatibilidade futura)
  edit: {
    book_name: {
      required: true,
      minValue: 3,
      maxValue: 200,
      errorMessage: 'Nome do livro deve ter entre 3 e 200 caracteres.',
    },
    isbn: {
      required: true,
      minValue: 10,
      maxValue: 17,
      errorMessage: 'ISBN deve ter entre 10 e 17 caracteres.',
    },
    arquivo: {
      required: false, // Na edição, o arquivo é opcional (pode manter o antigo)
      errorMessage: 'Arquivo inválido.',
    },
  },
}

const BookVectorizationValidate = new ValidateFormObject(bookVectorizationSchema)

// ============================================
// 🔄 REDUCER
// ============================================
const bookVectorization = (state = initialState, action = {}) => {
  let formSchemaType

  switch (action.type) {
    // ========================================
    // FORMULÁRIO - Mudança de campo
    // ========================================
    case CHANGE_BOOK_VECTORIZATION:
      formSchemaType = action.bookVectorization.id ? 'edit' : 'add'

      return {
        ...state,
        bookVectorization: action.bookVectorization,
        bookVectorizationFormErrors: BookVectorizationValidate.validate({
          formObject: action.bookVectorization,
          formSchemaType,
        }),
      }

    // ========================================
    // FORMULÁRIO - Erro de validação
    // ========================================
    case BOOK_VECTORIZATION_FORM_ERROR:
      const formErrors = {}
      formSchemaType = state.bookVectorization.id ? 'edit' : 'add'

      formErrors[action.field] = {
        valid: false,
        errorMessage: action.message,
      }

      return {
        ...state,
        bookVectorizationFormErrors: BookVectorizationValidate.validate({
          formObject: state.bookVectorization,
          invalidFields: formErrors,
          formSchemaType,
        }),
      }

    // ========================================
    // FORMULÁRIO - Novo (limpa tudo)
    // ========================================
    case NEW_FORM_BOOK_VECTORIZATION:
      return {
        ...state,
        bookVectorization: { ...initialState.bookVectorization },
        bookVectorizationFormErrors: BookVectorizationValidate.validate({}),
        bookVectorizationFormTouched: false,
      }

    // ========================================
    // FORMULÁRIO - Marca como "tocado"
    // ========================================
    case FORM_TOUCHED:
      return {
        ...state,
        bookVectorizationFormTouched: action.touched !== false,
      }

    // ========================================
    // ORDENAÇÃO - Muda coluna
    // ========================================
    case CHANGE_ORDER_COLUMN:
      return {
        ...state,
        order: {
          direction: 1,
          column: action.column,
        },
      }

    // ========================================
    // ORDENAÇÃO - Muda direção
    // ========================================
    case CHANGE_ORDER_DIRECTION:
      return {
        ...state,
        order: {
          ...state.order,
          direction: action.direction,
        },
      }

    // ========================================
    // MODAL - Abrir
    // ========================================
    case OPEN_MODAL:
      return {
        ...state,
        modalOpen: true,
      }

    // ========================================
    // MODAL - Fechar
    // ========================================
    case CLOSE_MODAL:
      return {
        ...state,
        modalOpen: false,
      }

    // ========================================
    // PAGINAÇÃO - Muda página
    // ========================================
    case CHANGE_PAGE:
      return {
        ...state,
        bookVectorizationsPagination: {
          ...state.bookVectorizationsPagination,
          page: action.page,
        },
      }

    // ========================================
    // PAGINAÇÃO - Muda itens por página
    // ========================================
    case CHANGE_PER_PAGE:
      return {
        ...state,
        bookVectorizationsPagination: {
          ...state.bookVectorizationsPagination,
          rowsPerPage: action.perPage,
        },
      }

    // ========================================
    // FETCH - Buscar UM livro (sucesso)
    // ========================================
    case FETCH_BOOK_VECTORIZATION:
      formSchemaType = (action.bookVectorization || {}).id ? 'edit' : 'add'

      return {
        ...state,
        bookVectorization: action.bookVectorization,
        loadingBookVectorization: false,
        fetchBookVectorizationError: false,
        bookVectorizationFormTouched: false,
        bookVectorizationFormErrors: BookVectorizationValidate.validate({
          formObject: action.bookVectorization,
          formSchemaType,
        }),
      }

    // ========================================
    // FETCH - Buscando UM livro (loading)
    // ========================================
    case FETCHING_BOOK_VECTORIZATION:
      return {
        ...state,
        loadingBookVectorization: true,
        fetchBookVectorizationError: false,
      }

    // ========================================
    // FETCH - Erro ao buscar UM livro
    // ========================================
    case FETCH_BOOK_VECTORIZATION_ERROR:
      return {
        ...state,
        loadingBookVectorization: false,
        bookVectorization: { ...initialState.bookVectorization },
        fetchBookVectorizationError: action.err,
      }

    // ========================================
    // FETCH - Buscar TODOS os livros (sucesso)
    // ========================================
    case FETCH_BOOK_VECTORIZATIONS:
      return {
        ...state,
        loadingBookVectorizations: false,
        bookVectorizations: action.bookVectorizations,
        fetchBookVectorizationsError: false,
        order: action.order,
        bookVectorizationsPagination: {
          length: action.length,
          page: action.page,
          rowsPerPage: action.rowsPerPage,
        },
      }

    // ========================================
    // FETCH - Buscando TODOS (loading)
    // ========================================
    case FETCHING_BOOK_VECTORIZATIONS:
      return {
        ...state,
        loadingBookVectorizations: true,
        fetchBookVectorizationsError: false,
      }

    // ========================================
    // FETCH - Erro ao buscar TODOS
    // ========================================
    case FETCH_BOOK_VECTORIZATIONS_ERROR:
      return {
        ...state,
        loadingBookVectorizations: false,
        fetchBookVectorizationsError: action.err,
        loadingBookVectorization: false,
      }

    // ========================================
    // ADD - Adicionar novo livro
    // ========================================
    case ADD_BOOK_VECTORIZATION:
      // Adiciona o novo livro no início da lista para feedback visual imediato
      const newList = [action.bookVectorization, ...state.bookVectorizations]

      return {
        ...state,
        loadingBookVectorizations: false,
        fetchBookVectorizationError: false,
        loadingBookVectorization: false,
        bookVectorizations: newList,
        // Opcional: Atualizar a paginação se necessário, mas geralmente um refresh resolve
      }

    // ========================================
    // EDIT - Editar livro existente
    // ========================================
    case EDIT_BOOK_VECTORIZATION:
      return {
        ...state,
        bookVectorizations: state.bookVectorizations.map((item) => {
          if (item.id === action.bookVectorization.id) {
            return action.bookVectorization
          }
          return item
        }),
        loadingBookVectorizations: false,
        fetchBookVectorizationError: false,
        loadingBookVectorization: false,
        fetchBookVectorizationsError: false,
      }

    // ========================================
    // DELETE - Deletar livro
    // ========================================
    case DELETE_BOOK_VECTORIZATION:
      return {
        ...state,
        loadingBookVectorizations: false,
        fetchBookVectorizationError: false,
        bookVectorizations: state.bookVectorizations.filter((item) => item.id !== action.id),
      }

    // ========================================
    // RESET - Volta ao estado inicial
    // ========================================
    case RESET_STATE:
      return initialState

    // ========================================
    // DEFAULT - Retorna estado atual
    // ========================================
    default:
      return state
  }
}

export default bookVectorization



// WEBPACK FOOTER //
// ./src/reducers/bookVectorization.js